import React, { useState, useEffect } from 'react';
import apiClient from '../config/axiosConfig';

interface BikeType {
  id: number;
  name: string;
  description?: string;
}

interface Location {
  id: number;
  name: string;
  city: string;
}

interface Bike {
  id: number;
  asset_code: string;
  bike_type_id: number;
  location_id: number;
  status: string;
  notes?: string;
  send_manager_email?: boolean;
  bike_type_name: string;
  location_name: string;
}

interface BikeEditFormProps {
  bike: Bike;
  onSave: (updatedFields: Partial<Bike>) => Promise<void>;
  onCancel: () => void;
  /** Alleen de locaties die deze beheerder mag beheren. */
  locations: Location[];
}

const BikeEditForm: React.FC<BikeEditFormProps> = ({ bike, onSave, onCancel, locations }) => {
  const [formData, setFormData] = useState({
    asset_code: bike.asset_code,
    bike_type_id: bike.bike_type_id,
    location_id: bike.location_id,
    status: bike.status,
    notes: bike.notes || '',
    send_manager_email: bike.send_manager_email ?? false
  });
  
  const [bikeTypes, setBikeTypes] = useState<BikeType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchBikeTypes();
  }, []);

  const fetchBikeTypes = async () => {
    try {
      const response = await apiClient.get('/bike-types');
      setBikeTypes(response.data.data || []);
    } catch (error) {
      // Error silently handled
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setFormData(prev => ({ ...prev, [name]: checked }));
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: name === 'bike_type_id' || name === 'location_id' ? parseInt(value) : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Only validate editable fields
    if (!formData.status) {
      newErrors.status = 'Status is verplicht.';
    }

    if (formData.notes && formData.notes.length > 500) {
      newErrors.notes = 'Opmerkingen mogen maximaal 500 tekens bevatten.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      setErrors({ submit: 'Failed to save bike. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bike-edit-form">
      <div className="bike-info-section">
        <div className="info-item">
          <span className="info-label">Asset Code:</span>
          <span className="info-value">{formData.asset_code}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Fiets Type:</span>
          <span className="info-value">
            {bikeTypes.find(type => type.id === formData.bike_type_id)?.name || 'Loading...'}
          </span>
        </div>
        <div className="info-item">
          <span className="info-label">Locatie:</span>
          <span className="info-value">
            {locations.find(location => location.id === formData.location_id)?.name || 'Loading...'} - {locations.find(location => location.id === formData.location_id)?.city || ''}
          </span>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="status">Status *</label>
        <select
          id="status"
          name="status"
          value={formData.status}
          onChange={handleInputChange}
          className={errors.status ? 'error' : ''}
          disabled={isLoading}
        >
          <option value="available">Beschikbaar</option>
          <option value="maintenance">Onderhoud</option>
          <option value="retired">Buiten gebruik</option>
        </select>
        {errors.status && <span className="error-message">{errors.status}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="notes">Notities</label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleInputChange}
          className={errors.notes ? 'error' : ''}
          rows={3}
          disabled={isLoading}
          placeholder="Optional notes about the bike..."
        />
        {errors.notes && <span className="error-message">{errors.notes}</span>}
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="send_manager_email"
            checked={formData.send_manager_email}
            onChange={handleInputChange}
            disabled={isLoading}
          />
          <span>Stuur bevestiging naar beheerder bij reservering</span>
        </label>
        <small className="form-help">
          Bij een nieuwe reservering van deze fiets krijgen de beheerders van deze locatie die e-mail
          aan hebben staan een bevestiging.
        </small>
      </div>

      {errors.submit && (
        <div className="error-message submit-error">
          {errors.submit}
        </div>
      )}

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
};

export default BikeEditForm;
